import { _ } from "libs";

import React, { ReactElement, useContext, useEffect, useState, useRef } from "react";
import queryString from "query-string";
import Stats from "./timeline/stats";
import GCPost from "./timeline/gcPost";
import Reservations from "./timeline/reservations";
import UserContext from "../contexts/userContext";
import stream from "getstream";
import { getStreamAuthToken } from "../actions";
import ErrorBoundary from "./errorBoundary";
import ClubPostItem from "./timeline/clubPost";
// import BuddyAccepted from "./timeline/buddyAccepted";
import MatchCreated from "./timeline/matchCreated";
import NewNetworkMember from "./timeline/newNetworkMember";
import MembershipCreated from "./timeline/membershipCreated";
import { InvisibleInfiniteLoader } from "./infiniteLoader";
import Loader from "./loader";
import adSelector from "../selectors/adSelector";
import withRedux from "../withRedux";
import { AdLocation, Ad, GcPost } from "../types";
import AdItemLarge from "./items/ad/large";
import OwnRatingUpdate from "./timeline/ownRatingUpdate";
import RatingUpdate from "./timeline/ratingUpdate";
import { lang } from "moment";

function timelineBlockFactory(activity, userId: string): ReactElement {
	const activities = activity.activities.filter((a) => {
		if (typeof a.actor === "object") {
			return a.actor.data != undefined;
		}
		return true;
	});

	if (activities.length === 0) {
		return null;
	}

	switch (activity.verb) {
		case "stats_created": {
			const item = activities[0];
			return (
				<Stats stats={item.stats.stats} startTime={item.stats.startDateTime} endTime={item.stats.endDateTime} />
			);
		}

		case "gc_post": {
			const item = activities[0];
			const post = filterGCPostLanguage(item.gcPost);
			if (!post) {
				return null;
			}
			return <GCPost post={post} activity={item} />;
		}

		case "federation_post": {
			const item = activities[0];
			const post = filterGCPostLanguage(item.federationPost);
			if (!post) {
				return null;
			}
			return <GCPost post={post} activity={item} />;
		}

		case "club_post": {
			const item = activities[0];
			return <ClubPostItem post={item.clubPost} activity={item} />;
		}

		case "user_reservation_created": {
			return <Reservations activities={activities} userId={userId} />;
		}

		// not displaying in the news feed for now
		case "buddy_accepted": {
			return null;
			// return <BuddyAccepted activities={activity.activities} onUserClick={onUserClick} />;
		}

		case "match_created": {
			return <MatchCreated activities={activities} />;
		}

		case "membership_created": {
			const memberShipActivities = activities.filter((a) => {
				if (typeof a.member === "object") {
					return a.member.data != undefined;
				}
				return false;
			});
			return <MembershipCreated activities={memberShipActivities} />;
		}

		case "new_network_member": {
			return <NewNetworkMember activities={activities} />;
		}

		case "own_rating_updated": {
			return <OwnRatingUpdate activities={activities} />;
		}

		case "rating_updated": {
			return <RatingUpdate activities={activities} userId={userId} />;
		}

		default: {
			return null; //don't show items which are not in the vocabulary above
		}
	}
}

const filterGCPostLanguage = (posts?: GcPost[]): GcPost => {
	const { profile } = useContext(UserContext);

	if (!posts?.length) {
		return null;
	}

	let { language, country } = profile;
	language = language?.toLowerCase() || "en";
	country = country?.toUpperCase() || "CH";
	const res = posts.filter((post, index) => {
		//return specific
		if (post.language === [language, country].join("_")) {
			return true;
		}

		if (post.language === ["*", country].join("_")) {
			return true;
		}

		if (post.language === [language, "*"].join("_")) {
			return true;
		}

		if (post.language === ["*", "*"].join("_")) {
			return true;
		}

		return false;
	});

	if (res.length === 0) {
		return null;
	}

	return res[0];
};

const Timeline = ({ limit = 10, ads = [], feedFrequency }: { limit?: number; ads: Ad[]; feedFrequency: number }) => {
	const { profile } = useContext(UserContext);

	const unmounted = useRef(false);

	const [data, updateActivities] = useState({ activities: [], next: null });
	let [streamClient, setStreamClient] = useState(null as stream.Client);
	const [loading, setLoading] = useState(true);
	const [inView, setInview] = useState(false);

	const activityCallback = (err, httpResponse, body: any) => {
		if (body) {
			if (body.results) {
				const parsed = queryString.parseUrl(body.next);
				updateActivities({ activities: data.activities.concat(body.results), next: parsed.query.id_lt });
			}
		}

		if (err) {
			console.log({ err, httpResponse });
		}

		setLoading(false);
	};

	const getClient = async (): Promise<stream.Client> => {
		return new Promise((resolve, reject) => {
			getStreamAuthToken()
				.then(async (token) => {
					const streamClient = stream.connect(
						process.env.REACT_APP_GETSTREAM_KEY,
						token,
						process.env.REACT_APP_GETSTREAM_APPID
					);

					await setStreamClient(streamClient);
					resolve(streamClient);
				})
				.catch((error) => reject(error));
		});
	};

	useEffect(() => {
		let ignore = false;
		getClient().then((streamClient) => {
			const timeline = streamClient.feed("user_timeline", profile.userId);

			if (!ignore) {
				let args = { limit, enrich: true };
				timeline.get(args, activityCallback);
			}
		});

		return () => {
			ignore = true;
		};
	}, [limit, profile.userId]);

	useEffect(() => {
		if (inView && data.next !== false && data.next !== null && data.next !== undefined && data.next !== "") {
			load();
		}
	}, [inView, data.next]);

	const load = async (client = null) => {
		if (client === null) {
			client = streamClient;
		}

		setLoading(true);

		const timeline = client.feed("user_timeline", profile.userId);
		const args = { limit, enrich: true, id_lt: data.next };
		try {
			const res = await timeline.get(args);
			activityCallback(null, null, res);
		} catch ({ error }) {
			if (error.detail === "Token is expired") {
				streamClient = await getClient();
				load(streamClient);
			}
		}

		setLoading(false);
	};

	const onViewChange = (inView: boolean, observerEntry) => {
		setInview(inView);
	};

	let items = [];

	let adIndex = 0;
	const adCount = ads ? ads.length : 0;

	for (let index in data.activities) {
		const itemNo = parseInt(index); //+1 if one does not want to start with an ad
		const activity = data.activities[parseInt(index)];

		if (adCount && itemNo % feedFrequency === 0) {
			const ad = ads[adIndex];

			items.push(
				<div className="naBlock" key={index}>
					<AdItemLarge ad={ad} />
				</div>
			);

			adIndex = adIndex + 1;
			if (adIndex >= adCount) {
				adIndex = 0;
			}
		}

		items.push(
			<div key={activity.id}>
				<ErrorBoundary>{timelineBlockFactory(activity, profile.userId)}</ErrorBoundary>
			</div>
		);
	}

	return (
		<div className="newsFeed">
			{items}
			{loading ? <Loader /> : <InvisibleInfiniteLoader onViewChange={onViewChange} />}
		</div>
	);
};

const mapStateToProps = ({ deals: { ads, feedFrequency } }, ownProps) => {
	return { ads: adSelector(ads, AdLocation.NewsStream), feedFrequency };
};

export default withRedux({}, mapStateToProps)(Timeline);
